import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, NOTIFICATION_TYPE } from '../../../../../constants/common'
import { useLinkLikeClick } from '../../../../../hooks'
import Button from '../../../../Button'
import { NotificationInline } from '../../../../Notification'
import { PageEmptyState } from '../../../../PageEmptyState'
import { getApplicantProfileLink } from '../../../../RentalPortalApplicant'
import { Table } from '../../../../Table'
import Text from '../../../../Text'
import {
  APPLICANT_DECISION_ACTION,
  ApplicantDecisionModal,
} from '../../../RentalPortalApplicantProfile'
import { useListingProfileContext } from '../RentalPortalListingProfile.hooks'

import {
  APPLICANT_COMPARISON_SKELETON_ROWS,
  QA_NAME_APPLICANT_COMPARISON_CONFLICT,
  QA_NAME_APPLICANT_COMPARISON_RETRY,
  QA_NAME_APPLICANT_COMPARISON_TABLE,
} from './RentalPortalApplicantComparison.constants'
import { useApplicantComparison } from './RentalPortalApplicantComparison.hooks'
import {
  getApplicantComparisonByColumn,
  getApplicantComparisonColumns,
  getApplicantComparisonRows,
  getApplicantComparisonState,
  getHasApplicantComparisonSelection,
  getIsApplicantComparisonEmpty,
  getIsApplicantComparisonError,
} from './RentalPortalApplicantComparison.utils'
import { RentalPortalApplicantComparisonRow } from './RentalPortalApplicantComparisonRow'

import * as styles from './RentalPortalApplicantComparison.module.css'

import RefreshIcon from '../../../../../icons/refresh.svg'
import RentalPortalIcon from '../../../../../icons/rental-portal.svg'
import UsersIcon from '../../../../../icons/users-1.svg'

function RentalPortalApplicantComparison() {
  const { t } = useTranslation()

  const { handleClick } = useLinkLikeClick()

  const { handleCloseModal, handleEntityReload, listingData, setModal } =
    useListingProfileContext()

  const containerRef = useRef(null)

  const {
    conflictKey,
    handleRefresh,
    hasError,
    isInitiated,
    isLoading,
    items,
    submitSelect,
  } = useApplicantComparison({ listingId: listingData?.id })

  function handleClickProfile(applicantId) {
    handleClick({ link: getApplicantProfileLink(applicantId) })
  }

  async function handleSubmitSelect({ applicantId, note }) {
    await submitSelect({ applicantId, note })

    handleCloseModal()

    await handleEntityReload()
  }

  function handleClickSelect(applicant) {
    setModal(
      <ApplicantDecisionModal
        action={APPLICANT_DECISION_ACTION.select}
        applicantData={applicant}
        onClose={handleCloseModal}
        onSubmit={note =>
          handleSubmitSelect({ applicantId: applicant.id, note })
        }
      />
    )
  }

  const state = getApplicantComparisonState({ hasError, isInitiated, items })

  return (
    <div className={styles.RentalPortalApplicantComparison} ref={containerRef}>
      {conflictKey ? (
        <NotificationInline
          qaName={QA_NAME_APPLICANT_COMPARISON_CONFLICT}
          text={t(conflictKey)}
          type={NOTIFICATION_TYPE.info}
        />
      ) : null}
      {getIsApplicantComparisonError(state) ? (
        <PageEmptyState EntityIcon={RentalPortalIcon}>
          <Text isSecondary>
            {t('RentalPortal.ApplicantComparisonLoadErrorKey')}
          </Text>
          <Button.Flat
            beforeIconProps={{ Icon: RefreshIcon }}
            isLoading={isLoading}
            qaName={QA_NAME_APPLICANT_COMPARISON_RETRY}
            text={t('RentalPortal.ListingRetryBtnKey')}
            onClick={handleRefresh}
          />
        </PageEmptyState>
      ) : getIsApplicantComparisonEmpty(state) ? (
        <PageEmptyState
          EntityIcon={UsersIcon}
          text={t('RentalPortal.ApplicantComparisonEmptyKey')}
        />
      ) : (
        <Table
          TableRow={RentalPortalApplicantComparisonRow}
          actionsColumnWidth={0}
          columns={getApplicantComparisonColumns({
            items,
            labelClassName: styles.headerLabelCell,
            t,
          })}
          containerRef={containerRef}
          context={QA_NAME_APPLICANT_COMPARISON_TABLE}
          headerRowProps={{ resizable: false }}
          isLoading={isLoading}
          items={isInitiated ? getApplicantComparisonRows() : EMPTY_LIST}
          rowProps={{
            applicantByColumn: getApplicantComparisonByColumn(items),
            hasSelection: getHasApplicantComparisonSelection(items),
            onClickProfile: handleClickProfile,
            onClickSelect: handleClickSelect,
          }}
          skeletonProps={{ withCheckbox: false }}
          skeletonRows={APPLICANT_COMPARISON_SKELETON_ROWS}
        />
      )}
    </div>
  )
}

export default RentalPortalApplicantComparison
